import { GetServerSideProps } from 'next/types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Article, Category } from '../../../backend/models';
import { ArticlesMenu } from '../../../components/ArticlesMenu';
import { SectionLayout } from '../../../components/layouts/SectionLayout';
import { KbContext } from '../../../frontend/KbContext';
import { handleAndGetTenantAsync, PageBaseProps } from '../../../utils/tenants';
import treelizeCategories from '../../../utils/treelizeCategories';
// hljs will be dynamically imported
import { getAllCategories, getCategoryBySlug } from '../../../backend/queries/categories';
import { getAllArticles } from '../../../backend/queries/articles';
import Head from 'next/head';
import { getFileUrl } from '../../../utils/imaging';
import { useRouter } from 'next/router';
import { useLightbox } from '../../../components/hooks/useLightbox';
import { getCanonicalURL } from '../../../utils/seo/getCanonicalURL';
import { removeQueryParamsFromRouter } from '../../../utils/localeHandling';
import Link from 'next/link';
import { AppContext } from '../../../frontend/AppContext';
import { makeUrlForArticle, makeUrlForCategory } from '../../../utils/urlizer';
import { Footer } from '../../_app';
import { ShouldBeRedirected } from '..';
import { sanitizeTenant } from '../../../utils/sanitizeTenant';


type Props = PageBaseProps & {
    allCategories: Array<Category>,
    allArticles: Array<Article>,
    category: Category;
};

const allowedHeaders = ['H2', 'H3', 'H4', 'H5', 'H6', 'H7'];

export default function CategoryPage(props: Props) {
    const [articleDomRef, setArticleDomRef] = useState<HTMLBaseElement | null>(null);
    const router = useRouter();

    const isEmbedded = router.query.embed == '1';
    const noNav = router.query.nonav == '1';

    removeQueryParamsFromRouter(router, ['r', 'lang_selector']); // remove the "r"

    const categories = treelizeCategories(props.allCategories);
    const articles = props.allArticles.filter(x => x.categoryId == props.category.id);

    useEffect(() => {
        import('highlight.js').then((hljs) => {
            hljs.default.highlightAll();
        });
    }, [props.category?.id]);

    const articleContentRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            // parse headers
            try {
                const allHeaders = Array.from(node.querySelectorAll(allowedHeaders.join(',')));
                allHeaders
                    .forEach(el => {
                        el.setAttribute('id', el.textContent?.toLocaleLowerCase().replaceAll(' ', '-').replaceAll('#', '-') || '');
                        el.classList.add('hashed-header');
                    });

                attachHeadersHashButtons(allHeaders);
            } catch (e) { }

            // create lightbox
            try {
                const allImages = Array.from(node.querySelectorAll('img'));

                allImages.forEach(imgEl => {
                    imgEl.dataset.lghtb = 'true';
                });
            } catch (e) { }
        }
    }, [props.category?.id]);
    const appContext = useContext(AppContext);
    const treelizedCategory = categories.find(x => x.id == props.category.id);

    const LightBox = useLightbox();

    return <KbContext.Provider value={{ allArticles: props.allArticles, allCategories: props.allCategories, treelizedCategories: categories }}>
        <Head>
            <title>{`${props.category?.title} - ${props.tenant.title}`}</title>
            <link rel='icon' type='image/png' href={getFileUrl(props.tenant.icons.favIconFileId)} />
            {props.canonical?.usingCustomDomain && <link rel='canonical' href={props.canonical?.canonicalUrl} />}
            <meta property="og:image" content={getFileUrl(props.tenant.icons.logoFileId)}></meta>
        </Head>
        <SectionLayout isEmbed={isEmbedded}>
            <div className='mt-10'>
                <div className={`${noNav ? '' : 'content-grid'} w-full`}>
                    {!noNav && <div className="h-full menu-column border-r border-r-slate-300">
                        <div className={`fixed bottom-0 overflow-auto mb-10 z-20 ${!isEmbedded ? 'top-[7rem]' : 'top-10'}`}>
                            <ArticlesMenu categories={categories}
                                articles={props.allArticles}
                                selectedCategory={props.category} />
                        </div>
                    </div>}
                    <div className="px-8 pr-10 article ck-content shrink flex flex-col gap-4"
                        ref={(r) => setArticleDomRef(r as unknown as HTMLBaseElement)}>

                        <h1 className='font-extrabold text-4xl mb-5'>{props.category?.title}</h1>
                        <div className='text-2xl mb-5'>{props.category?.description}</div>
                        {articles.length > 0 && <div className='w-full block transition-all shadow-sm p-8 rounded-xl bg-white shadow-sm flex flex-col gap-1'>
                            {articles.sort((a, b) => {
                                return a.order - b.order;
                            }).map((x, i) => {
                                if (!appContext) {
                                    return "";
                                }
                                return <div key={i} className='flex justify-between hover:text-primary-bg w-full cursor-pointer'>

                                    <Link href={makeUrlForArticle(appContext.tenant.currentLocale, x, {
                                        isEmbedMode: appContext.tenant.isEmbedMode,
                                        isNoNavMode: appContext.tenant.isNoNavMode
                                    })}>
                                        <div className='flex justify-between w-full'>
                                            <div className='' >{x.publishedContent.title}</div>
                                            <span className={`inline-block transition-all`}><i className="fa-solid fa-chevron-right text-primary-bg"></i></span>
                                        </div>
                                    </Link>
                                </div>;
                            })}
                        </div>
                        }
                        {treelizedCategory?.children?.map((x, i) => {
                            const children = props.allArticles.filter(f => f.categoryId == x.id);
                            if (!appContext) {
                                return "";
                            }
                            if (!children || children.length <= 0) {
                                return "";
                            }
                            return <div key={i} className='w-full m-0 block transition-all shadow-sm p-8 rounded-xl bg-white flex flex-col gap-1'>
                                <Link href={makeUrlForCategory(appContext.tenant.currentLocale, x, {
                                    isEmbedMode: appContext.tenant.isEmbedMode,
                                    isNoNavMode: appContext.tenant.isNoNavMode
                                })}>
                                    <h2 className='!mt-0 hover:text-primary-bg cursor-pointer'>{x.title}</h2>
                                </Link>
                                <hr style={{ height: 1 }} />
                                {children.sort((a, b) => {
                                    return a.order - b.order;
                                }).map((y, index) => {
                                    if (!appContext) {
                                        return "";
                                    }
                                    return <div key={index} className='flex justify-between hover:text-primary-bg cursor-pointer'>

                                        <Link href={makeUrlForArticle(appContext.tenant.currentLocale, y, {
                                            isEmbedMode: appContext.tenant.isEmbedMode,
                                            isNoNavMode: appContext.tenant.isNoNavMode
                                        })}>
                                            <div className='flex justify-between w-full'>
                                                <div className='' >{y.publishedContent.title}</div>
                                                <span className={`inline-block transition-all`}><i className="fa-solid fa-chevron-right text-primary-bg"></i></span>
                                            </div>
                                        </Link>
                                    </div>;
                                })}
                            </div>;
                        })}
                    </div>
                </div>
            </div>
            {LightBox}
        </SectionLayout>
        <Footer slugs={props.category.slugs} />
    </KbContext.Provider>;
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
    const { tenant, auth, notFound } = await handleAndGetTenantAsync(ctx, true);

    if (notFound) {
        return {
            notFound: true
        };
    }

    if (!auth.isFromAllowedIP) {
        return {
            notFound: true
        };
    }

    const isFromLangDropdown = ctx.query.lang_selector == "true";

    let locale = (ctx.params?.language ?? tenant.languagesHandling.defaultLanguage ?? 'en') as string;

    if (!auth?.isAuthorized) {

        return {
            redirect: {
                permanent: false,
                destination: `/${locale}/auth/signin/?return=/${locale}${ctx?.resolvedUrl?.replace(/^\/([a-z]{2})\//, '/') ?? ""}&protected=true`,
            },
            props: {},
        };
    }
    const result = ShouldBeRedirected(ctx, tenant);
    if (result) return result;

    const categorySlug = (ctx.query['slug'] as string[])[0];

    const cats = await getAllCategories(tenant);
    const allArticles = await getAllArticles(tenant, auth?.isAuthenticated);
    const category = await getCategoryBySlug(tenant, categorySlug);

    if (!category) {
        if (isFromLangDropdown) {
            return {
                redirect: {
                    permanent: false,
                    destination: `/${locale}`,
                },
                props: {},
            };
        }
        return {
            notFound: true
        };
    }

    const canonical = getCanonicalURL(tenant, ctx.resolvedUrl);

    return {
        props: {
            allCategories: cats,
            allArticles: allArticles,
            tenant: sanitizeTenant(tenant),
            category: category,
            auth,
            canonical
        }
    };
};

let hashBtnInitialized = false;
function attachHeadersHashButtons(headers: Array<Element>) {
    let hashBtn: HTMLElement = document.createElement('div');

    let currentHeaderId = '';

    if (!hashBtnInitialized || !document.contains(hashBtn)) {
        hashBtn.id = 'hash-btn';
        hashBtn.textContent = '#';
        hashBtn.addEventListener('click', () => {
            window.location.hash = currentHeaderId;
        });

        hashBtnInitialized = true;
    }

    for (const header of headers) {
        header.addEventListener('mouseenter', (e) => {
            const target = e.target as HTMLElement;

            if (target && target.classList && target.classList.contains('hashed-header')) {
                hashBtn.style.top = '2px';
                hashBtn.style.left = '-40px';
                hashBtn.style.display = 'flex';

                currentHeaderId = target.id;
                target.appendChild(hashBtn);
            }
        }, { passive: true });

        header.addEventListener('mouseleave', (e) => {
            hashBtn.style.display = 'none';
            hashBtn.remove();
            currentHeaderId = '';
        });
    }
}
